import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { hasBreadcrumbTrail, type Crumb } from '@/seo/structuredData';

/*
 * Deliberately not re-exported from `./index`: this component imports the
 * `@/seo` builders, and the barrel is also loaded by the entry chunk (the
 * footer), which must not pull them in. Import it from
 * `@/components/ui/Breadcrumbs`.
 */

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

export interface BreadcrumbsProps {
  /**
   * The page head's `breadcrumbs` (`@/seo/templates`): the very array its
   * BreadcrumbList JSON-LD is built from. Pass it unchanged.
   */
  crumbs: readonly Crumb[] | null | undefined;
  className?: string;
}

/**
 * The visible breadcrumb trail (web plan W0.8, master D-8).
 *
 * Rendered from the same crumb array as the page's BreadcrumbList, under the
 * same rule (`hasBreadcrumbTrail`), so the two always carry the same names and
 * URLs in the same order and never exist one without the other. Every crumb is
 * a real link; the last one is the current page (`aria-current="page"`), as in
 * the WAI-ARIA breadcrumb pattern. The chevrons are decorative, so a screen
 * reader hears only the list of links.
 *
 * Long names (a promotion headline) stay on one line and are cut with an
 * ellipsis at the container's width; the full name is still the link's text.
 * Each link's line box is 24 px (`leading-6`), the minimum target size of
 * WCAG 2.5.8: at `text-sm`'s own 20 px, Lighthouse flags the crumbs.
 */
export function Breadcrumbs({ crumbs, className }: BreadcrumbsProps) {
  if (!hasBreadcrumbTrail(crumbs)) return null;
  const last = crumbs.length - 1;
  return (
    <nav aria-label="Breadcrumb" className={cn('text-sm text-ink-secondary', className)}>
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
        {crumbs.map((crumb, i) => {
          const current = i === last;
          return (
            // Index in the key: two crumbs may share a path (e.g. a list and its only page).
            <li key={`${i}:${crumb.path}`} className="flex min-w-0 max-w-full items-center gap-1">
              {i > 0 && (
                <ChevronRight size={14} aria-hidden="true" className="shrink-0 text-ink-tertiary" />
              )}
              <Link
                to={crumb.path}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  'truncate leading-6 transition-colors duration-200 hover:text-primary-strong',
                  current && 'font-semibold text-ink',
                )}
              >
                {crumb.name}
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
